'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// useLayoutEffect avisa en SSR ("does nothing on the server"); en el navegador sí lo
// queremos (mide y ajusta antes del paint, sin parpadeo). El patrón isomórfico usa
// useEffect en el server y useLayoutEffect en el cliente.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface AutoFitValueProps {
	value: string;
	/** Tamaño inicial (usado en SSR y primer paint): normalmente el heurístico por
	 *  caracteres, para que el salto al medir sea imperceptible. */
	initialFontSize: string;
	/** Tope superior en px: nunca agranda por encima del tamaño base de diseño. */
	maxPx?: number;
	/** Piso inferior en px: no achica más allá de esto (legibilidad). */
	minPx?: number;
	className?: string;
	title?: string;
}

// Ajusta la fuente del VALOR (una sola línea, nowrap) al ancho REAL de su columna:
// busca el mayor tamaño que entra sin desbordar, medido con el DOM. Reemplaza al
// cálculo por caracteres (`valueFontSize`) cuando el ancho real difiere del estimado
// —típico en el grid del perfil, donde la misma columna cambia de ancho por el span.
// El binary search corre dentro de useLayoutEffect (antes del paint) → sin parpadeo.
export function AutoFitValue({
	value,
	initialFontSize,
	maxPx = 13.8,
	minPx = 9,
	className,
	title,
}: AutoFitValueProps) {
	const ref = useRef<HTMLParagraphElement>(null);
	const [fontSize, setFontSize] = useState(initialFontSize);

	useIsomorphicLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;

		// Recalcula solo si cambió el ANCHO de la caja (cambiar la fuente altera la
		// altura y dispararía el observer en bucle; comparar el ancho lo evita).
		let lastWidth = -1;
		const fit = () => {
			const width = el.clientWidth;
			if (width === lastWidth) return;
			lastWidth = width;

			// Mayor font-size (px) cuyo texto entra en el ancho del <p> (nowrap →
			// scrollWidth es el ancho natural del texto; clientWidth el de la columna).
			let lo = minPx;
			let hi = maxPx;
			for (let i = 0; i < 8; i++) {
				const mid = (lo + hi) / 2;
				el.style.fontSize = `${mid}px`;
				if (el.scrollWidth <= el.clientWidth) lo = mid;
				else hi = mid;
			}
			el.style.fontSize = `${lo}px`;
			setFontSize(`${lo}px`);
		};

		fit();
		const observer = new ResizeObserver(fit);
		observer.observe(el);
		return () => observer.disconnect();
	}, [value, maxPx, minPx]);

	return (
		<p ref={ref} className={className} style={{ fontSize }} title={title ?? value}>
			{value}
		</p>
	);
}
